import { Controller } from '@hotwired/stimulus';
import fitAutoresizeTextareas from 'helpers/autoresize';
import { fillRegion, isPlainKey, nameSegments, placeInOrder, writeField } from 'helpers/form-region';
import recheckRangePairs from 'helpers/goals-v3-range-pairs';

// Stimulus controller for the Goals v3 smart form control.
//
// Responsibilities:
//   - Add / remove Long Term Goals and Short Term Goals (template clone + DOM insertion).
//   - Ask before a saved goal comes off the document (a modal laid out like
//     the shared confirmation modal); a row not saved yet goes at once.
//   - Swap the measurement region when the framework <select> changes, by
//     fetching the new category sub-partial and replacing the region's
//     innerHTML. We can't use <turbo-frame> here because the main app pack
//     loads Turbolinks (legacy), not Turbo.
//   - Put the goals back from a copy auto-save kept (restore).
//
// Removal is DOM-only: the reconciliation service treats absence from the
// submitted form_data as "the user removed this from the document," not as
// "void the underlying goal." A saved goal nobody has signed yet that loses
// its draft that way is withdrawn and offered back under "Goals taken off
// before signing". Goal lifecycle (void/discontinue/met) is a separate
// concern handled by Patient::GoalsController.
export default class extends Controller {
  static targets = [
    'canvas',
    'ltgTemplate',
    'longTermGoal',
    'shortTermGoal',
    'frameworkSelect',
    'measurementRegion',
    'goalsMasterToggle',
    'takeOffModal',
    'takeOffTitle',
    'takeOffMessage',
  ];

  static values = { controlId: String, nextLtgSlot: { type: Number, default: 1 } };

  // A collapsed row hides its inputs (display:none, never disabled), and the
  // browser cannot focus or bubble on a hidden invalid control — native
  // validation would fail the save silently. The capture-phase invalid
  // listener re-opens the collapsed row hiding the control the moment
  // validation flags it (and shows a row "Customize Goals Shown" hides), so
  // the browser's focus + message land on a visible field.
  //
  // It also numbers the new rows right away: a rejected save re-renders the
  // goals the therapist added, and their previews would otherwise read a bare
  // "LTG" / "STG" until the next add or remove. And it checks the trial pairs
  // the page opens with, so a value a save refused is flagged again.
  connect() {
    this._onInvalid = (event) => this._revealInvalidControl(event.target);
    this.element.addEventListener('invalid', this._onInvalid, true);
    // The saved goals taken off since the page loaded, by key, each row kept
    // whole: a copy auto-save made before one came off brings it back (restore).
    this._takenOff = new Map();
    this._renumberPreviews();
    this.element.querySelectorAll('[data-goals-v3-trials-pair]').forEach((pair) => this._checkTrialsPair(pair));
  }

  disconnect() {
    this.element.removeEventListener('invalid', this._onInvalid, true);
    this._takenOff = new Map();
    this._pendingTakeOff = null;
    const modal = this._goalBankModal();
    if (modal && this._bankWired) {
      modal.removeEventListener('keydown', this._onBankKeydown);
      modal.removeEventListener('input', this._onBankInput);
      modal.removeEventListener('click', this._onBankClick);
      this._bankWired = false;
    }
  }

  _revealInvalidControl(control) {
    if (!control || !control.closest) return;
    const shown = [];

    // A row "Customize Goals Shown" hides is display:none too. The rows
    // holding the flagged field show again, a short-term goal's long-term
    // goal included, so the browser can point at it; the saved choice itself
    // is left alone.
    let hiddenRow = control.closest('.gv3-presentation-hidden');
    while (hiddenRow && this.element.contains(hiddenRow)) {
      hiddenRow.classList.remove('gv3-presentation-hidden');
      shown.push(hiddenRow);
      hiddenRow = hiddenRow.parentElement && hiddenRow.parentElement.closest('.gv3-presentation-hidden');
    }

    let collapsible = control.closest('.goals-v3-goal-collapsible.hidden');
    while (collapsible) {
      collapsible.classList.remove('hidden');
      shown.push(collapsible);
      const card = collapsible.closest('.card');
      const header = card && card.querySelector(':scope > .gv3-expand-row');
      if (header) {
        header.classList.add('is-open');
        const disclosure = header.querySelector('[data-goals-v3-editor-target="goalDisclosure"]');
        if (disclosure) disclosure.setAttribute('aria-expanded', 'true');
      }
      collapsible = collapsible.parentElement && collapsible.parentElement.closest('.goals-v3-goal-collapsible.hidden');
    }
    // The goal text shown now takes the size of what it holds: the page sized
    // it while it was out of sight.
    if (shown.length) fitAutoresizeTextareas(...shown);
    this._syncGoalsMasterCaret();
  }

  // --- LTG add ---------------------------------------------------------

  addLongTermGoal(event) {
    event.preventDefault();
    if (!this.hasLtgTemplateTarget) return;

    const key = this._generateKey();
    const html = this.ltgTemplateTarget.innerHTML.replaceAll('__KEY__', key);
    this.canvasTarget.insertAdjacentHTML('beforeend', html);

    const noGoalsMessage = this.element.querySelector('.no-long-term-goals');
    if (noGoalsMessage) noGoalsMessage.classList.add('hidden');

    this._renumberPreviews();
  }

  // --- STG add ---------------------------------------------------------

  addShortTermGoal(event) {
    event.preventDefault();
    const parentKey = event.params.key;
    if (!parentKey) return;

    const template = this.element.querySelector(`template.goals-v3-stg-template[data-parent-key="${parentKey}"]`);
    if (!template) return;

    const stgCanvas = this.element.querySelector(`[data-goal-key="${parentKey}"] .short-term-goals-canvas`);
    if (!stgCanvas) return;

    const stgKey = this._generateKey();
    const html = template.innerHTML.replaceAll('__STG_KEY__', stgKey);
    stgCanvas.insertAdjacentHTML('beforeend', html);

    // The new row starts no earlier than its long-term goal, as the start box reads now (the
    // template was drawn with the value the page loaded with).
    const parentEl = stgCanvas.closest('[data-goal-key]');
    const parentStart = parentEl ? this._ownElement(parentEl, 'input[name$="[start_date]"]') : null;
    const newRow = stgCanvas.querySelector(`[data-goal-key="${stgKey}"]`);
    if (parentStart && newRow) this._setFloor(this._ownElement(newRow, 'input[name$="[start_date]"]'), parentStart.value);

    this._renumberPreviews();
  }

  // --- Add back a withdrawn goal ---------------------------------------
  //
  // A goal withdrawn before anyone signed it (taken off the document writing
  // it and saved, or on a document deleted before it signed) is offered
  // under "Goals taken off before signing": its whole row waits in a
  // <template>, rendered from its last draft with its own number. Adding it
  // back moves the row onto the page (a short-term goal under its long-term
  // goal) and saving this document stages it again.

  addBackGoal(event) {
    event.preventDefault();
    const { goalId, parentKey } = event.params;
    if (!goalId) return;

    const template = this.element.querySelector(`template[data-withdrawn-goal-template="${goalId}"]`);
    const target = parentKey
      ? this.element.querySelector(`[data-goal-key="${parentKey}"] .short-term-goals-canvas`)
      : this.canvasTarget;
    if (!template || !target) return;

    target.insertAdjacentHTML('beforeend', template.innerHTML);
    this._setWithdrawnOffered(goalId, false);

    if (!parentKey) {
      const noGoalsMessage = this.element.querySelector('.no-long-term-goals');
      if (noGoalsMessage) noGoalsMessage.classList.add('hidden');
    }

    this._renumberPreviews();
  }

  // An added-back goal taken off again before the save goes back on offer
  // (a long-term goal's short-term goals ride with its offer).
  _reofferWithdrawn(node) {
    [node, ...node.querySelectorAll('[data-withdrawn-goal-id]')].forEach((row) => {
      if (row.dataset.withdrawnGoalId) this._setWithdrawnOffered(row.dataset.withdrawnGoalId, true);
    });
  }

  _setWithdrawnOffered(goalId, offered) {
    const offer = this.element.querySelector(`[data-withdrawn-goal-offer="${goalId}"]`);
    if (!offer) return;
    offer.hidden = !offered;

    const section = offer.closest('[data-withdrawn-goals]');
    if (!section) return;
    const count = section.querySelectorAll('[data-withdrawn-goal-offer]:not([hidden])').length;
    const countLabel = section.querySelector('[data-withdrawn-goal-count]');
    if (countLabel) countLabel.textContent = String(count);
    section.hidden = count === 0;
  }

  // --- LTG / STG remove -----------------------------------------------
  //
  // Fired by the trash on the rows this document can take off: a new row, a
  // goal added back from "Goals taken off before signing", and a saved goal
  // this document is writing that nobody has signed yet (its row carries
  // data-gv3-take-off-saved). A new or added-back row goes at once: nothing
  // saved on this document goes with it, and an added-back goal goes back on
  // offer. A saved goal asks first: taking it off drops it from what the form
  // submits, and the save then abandons this document's draft of it, so the
  // goal and the short-term goals in its row are withdrawn until someone
  // adds them back. Goals in force never show the trash; their lifecycle
  // changes are the status dropdown or Patient::GoalsController.

  removeLongTermGoal(event) {
    this._requestTakeOff(event);
  }

  removeShortTermGoal(event) {
    this._requestTakeOff(event);
  }

  _requestTakeOff(event) {
    event.preventDefault();
    const { key } = event.params;
    if (!key) return;
    const row = this._goalRow(key);
    if (!row) return;

    if (!this._savedTakeOff(row) || !this.hasTakeOffModalTarget) {
      this._takeOffRow(row);
      return;
    }

    this._pendingTakeOff = key;
    this.takeOffTitleTarget.textContent = `Take ${this._goalLabel(row)} off this document?`;
    this.takeOffMessageTarget.textContent = this._takeOffMessage(row);
    this._showTakeOffModal();
  }

  confirmTakeOff() {
    const key = this._pendingTakeOff;
    this._pendingTakeOff = null;
    this._hideTakeOffModal();
    const row = key ? this._goalRow(key) : null;
    if (row) this._takeOffRow(row);
  }

  // Cancel, and closing the modal any other way (Escape, the backdrop), keeps
  // the goal.
  cancelTakeOff() {
    this._pendingTakeOff = null;
    this._hideTakeOffModal();
  }

  // What coming off takes with it: a long-term goal's short-term goals, the
  // ones written on the page since it loaded included. The goal is offered
  // back once the document is saved.
  _takeOffMessage(row) {
    const isLtg = row.matches('[data-goals-v3-editor-target="longTermGoal"]');
    const stgCanvas = isLtg ? this._ownElement(row, '.short-term-goals-canvas') : null;
    const stgs = stgCanvas ? this._rowsIn(stgCanvas).length : 0;
    const back = 'Once you save, you can add it back from "Goals taken off before signing".';
    if (stgs === 0) return back;
    if (stgs === 1) return `The short-term goal under it comes off too. ${back}`;
    return `The ${stgs} short-term goals under it come off too. ${back}`;
  }

  // The coordinate the row's header shows ("LTG 1.0.0").
  _goalLabel(row) {
    const label = this._ownElement(row, '[data-gv3-goal-label]');
    const text = label ? label.textContent.replace(/\s+/g, ' ').trim() : '';
    return text || 'this goal';
  }

  _showTakeOffModal() {
    const modal = this.takeOffModalTarget;
    const $ = window.jQuery || window.$;
    if ($ && $.fn && $.fn.modal) {
      $(modal).one('hidden.bs.modal', () => {
        this._pendingTakeOff = null;
      });
      $(modal).modal('show');
    } else {
      modal.classList.add('show');
      modal.style.display = 'block';
    }
  }

  _hideTakeOffModal() {
    const modal = this.takeOffModalTarget;
    const $ = window.jQuery || window.$;
    if ($ && $.fn && $.fn.modal) {
      $(modal).modal('hide');
    } else {
      modal.classList.remove('show');
      modal.style.display = 'none';
    }
  }

  _takeOffRow(row) {
    this._detachRow(row);

    if (this.canvasTarget.querySelectorAll('[data-goals-v3-editor-target="longTermGoal"]').length === 0) {
      const noGoalsMessage = this.element.querySelector('.no-long-term-goals');
      if (noGoalsMessage) noGoalsMessage.classList.remove('hidden');
    }

    this._renumberPreviews();
  }

  // Takes a goal's row off the page, with the short-term goals in it. An
  // added-back goal goes back on offer. A saved goal's row is kept aside, so
  // a copy auto-save made before it came off can bring it back (restore). A
  // short-term goal waiting under a long-term goal taken off has nowhere to
  // go, so its offer comes down until that goal is back.
  _detachRow(row) {
    const key = row.dataset.goalKey;
    this._reofferWithdrawn(row);
    if (this._savedTakeOff(row)) this._takenOff.set(key, row);
    row.remove();
    if (row.matches('[data-goals-v3-editor-target="longTermGoal"]')) this._syncOffersUnder(key);
  }

  // A saved goal this document is writing, which the trash can take off.
  _savedTakeOff(row) {
    return row.dataset.gv3TakeOffSaved !== undefined;
  }

  _goalRow(key) {
    return this.element.querySelector(`[data-goal-key="${key}"]`);
  }

  // A short-term goal's offer puts its row under its long-term goal's, so it
  // is offered while that row is on the page and its own is not.
  _syncOffersUnder(parentKey) {
    const parentOnPage = this._goalRow(parentKey) !== null;
    this.element.querySelectorAll('[data-withdrawn-goal-offer]').forEach((offer) => {
      const button = offer.querySelector('[data-goals-v3-editor-parent-key-param]');
      if (!button || button.getAttribute('data-goals-v3-editor-parent-key-param') !== parentKey) return;
      const goalId = offer.dataset.withdrawnGoalOffer;
      this._setWithdrawnOffered(goalId, parentOnPage && this._goalRow(goalId) === null);
    });
  }

  // Fills the version preview on every NEW row with the coordinate it will
  // take when it saves — the discipline's next free LTG slot (or the
  // parent's next STG slot) plus the row's position among its new siblings,
  // walked in DOM order because that is submission order, which is the
  // order the reconciler allocates in. Persisted rows keep their real
  // coordinates and their slots anchor the arithmetic. Also keeps each
  // LTG header's STG-count badge honest.
  _renumberPreviews() {
    this._rememberFrameworks();
    if (!this.hasCanvasTarget) return;
    let nextLtg = this.nextLtgSlotValue || 1;

    this.canvasTarget.querySelectorAll('[data-goals-v3-editor-target="longTermGoal"]').forEach((ltgRow) => {
      const slot = ltgRow.dataset.goalSlot ? Number(ltgRow.dataset.goalSlot) : nextLtg++;
      const ltgPreview = ltgRow.querySelector('[data-goals-v3-editor-target="versionPreview"][data-tier="ltg"]');
      if (ltgPreview) ltgPreview.textContent = `LTG ${slot}.0.0`;

      let nextStg = ltgRow.dataset.nextStgSlot ? Number(ltgRow.dataset.nextStgSlot) : 1;
      let stgCount = 0;
      ltgRow.querySelectorAll('[data-goals-v3-editor-target="shortTermGoal"]').forEach((stgRow) => {
        stgCount += 1;
        const stgPreview = stgRow.querySelector('[data-goals-v3-editor-target="versionPreview"][data-tier="stg"]');
        if (stgPreview) stgPreview.textContent = `STG ${slot}.${nextStg++}.0`;
      });

      const badge = ltgRow.querySelector('[data-goals-v3-editor-target="stgCountBadge"]');
      if (badge) {
        badge.textContent = `${stgCount} STG${stgCount === 1 ? '' : 's'}`;
        badge.hidden = stgCount === 0;
      }
    });
  }

  // Toggles for the editor's Status history / Comments tabs. Both share the
  // tabs-section wrapper; each click walks downward through the wrapper's
  // siblings to find the matching collapsible panel. `.active` on the link
  // gives a visible open/closed state without rotating an icon.
  toggleStatusHistory(event) {
    this._toggleTabPanel(event, 'goals-v3-status-history-collapsible');
  }

  toggleComments(event) {
    this._toggleTabPanel(event, 'goals-v3-comments-collapsible');
  }

  _toggleTabPanel(event, panelClass) {
    event.preventDefault();
    const link = event.currentTarget;
    const tabsSection = link.closest('.goals-v3-history-tabs-section, .goals-v3-comments-section');
    if (!tabsSection) return;
    let panel = tabsSection.nextElementSibling;
    while (panel && !panel.classList.contains(panelClass)) {
      panel = panel.nextElementSibling;
    }
    if (!panel) return;
    const isHidden = panel.classList.toggle('hidden');
    link.classList.toggle('active', !isHidden);
  }

  // --- Row collapse ----------------------------------------------------

  // Click anywhere on a goal's header row toggles its detail (textarea,
  // Details band, comments, STG section). Collapsed content is hidden,
  // never disabled, so its inputs still submit. Clicks on the header's own
  // controls (the status select, the trash link — anything under
  // [data-goals-v3-row-ignore] or an interactive tag) never toggle. An
  // opened row sizes its goal text, which the page sized as it loaded: a
  // goal Customize Goals Shown hid was out of sight then.
  toggleGoalRow(event) {
    const header = event.currentTarget;
    if (event.target.closest('[data-goals-v3-row-ignore], select, a, input, textarea, awc-autocomplete')) return;

    const card = header.closest('.card');
    const detail = card && card.querySelector(':scope > .goals-v3-goal-collapsible');
    if (!detail) return;

    const open = !detail.classList.toggle('hidden');
    header.classList.toggle('is-open', open);
    const disclosure = header.querySelector('[data-goals-v3-editor-target="goalDisclosure"]');
    if (disclosure) disclosure.setAttribute('aria-expanded', String(open));
    if (open) fitAutoresizeTextareas(detail);
    this._syncGoalsMasterCaret();
  }

  // The label's expand/collapse-all twisty: any goal row tucked away means
  // open every row; otherwise close them all. Same class mechanics as the
  // per-row toggle, applied page-wide, and the opened rows size their goal
  // text the same way.
  toggleAllGoals(event) {
    event.preventDefault();
    const collapsibles = Array.from(this.element.querySelectorAll('.goals-v3-goal-collapsible'));
    if (!collapsibles.length) return;

    const opening = collapsibles.some((collapsible) => collapsible.classList.contains('hidden'));
    collapsibles.forEach((collapsible) => {
      collapsible.classList.toggle('hidden', !opening);
      const card = collapsible.closest('.card');
      const header = card && card.querySelector(':scope > .gv3-expand-row');
      if (!header) return;
      header.classList.toggle('is-open', opening);
      const disclosure = header.querySelector('[data-goals-v3-editor-target="goalDisclosure"]');
      if (disclosure) disclosure.setAttribute('aria-expanded', String(opening));
    });
    if (opening) fitAutoresizeTextareas(...collapsibles);
    this._syncGoalsMasterCaret();
  }

  // The master caret reads open only while every goal row is open, so
  // per-row clicks and the invalid-reveal keep it honest.
  _syncGoalsMasterCaret() {
    if (!this.hasGoalsMasterToggleTarget) return;
    const collapsibles = Array.from(this.element.querySelectorAll('.goals-v3-goal-collapsible'));
    const open =
      collapsibles.length > 0 && collapsibles.every((collapsible) => !collapsible.classList.contains('hidden'));
    this.goalsMasterToggleTarget.setAttribute('aria-expanded', String(open));
    const caret = this.goalsMasterToggleTarget.querySelector('.gv3-expand-caret');
    if (caret) caret.classList.toggle('is-open', open);
  }

  // Keeps the header's ellipsized goal-text preview in step with the
  // textarea, so a collapsed row still says what the goal is.
  syncGoalPreview(event) {
    const goalEl = event.currentTarget.closest('[data-goal-key]');
    if (goalEl) this._showPreview(goalEl, event.currentTarget.value);
  }

  // A goal's target date comes no earlier than its start date, and a short-term goal starts no
  // earlier than its long-term goal: the target box's earliest day follows the start box as it is
  // typed, and a long-term goal's start box moves the floor of every short-term goal's start box
  // under it, so the pickers stop there (the save refuses the pairs too,
  // DocumentReconciler.date_problems). A cleared start date lifts the floors it set.
  syncTargetDateMin(event) {
    const goalEl = event.currentTarget.closest('[data-goal-key]');
    if (!goalEl) return;

    const floor = event.currentTarget.value;
    this._setFloor(this._ownElement(goalEl, 'input[name$="[target_date]"]'), floor);
    this._shortTermRows(goalEl).forEach((row) => this._setFloor(this._ownElement(row, 'input[name$="[start_date]"]'), floor));
  }

  _setFloor(input, floor) {
    if (!input) return;
    if (floor) input.min = floor;
    else input.removeAttribute('min');
  }

  _shortTermRows(goalEl) {
    const canvas = this._ownElement(goalEl, '.short-term-goals-canvas');
    return canvas ? Array.from(canvas.querySelectorAll('[data-goal-key]')) : [];
  }

  _showPreview(goalEl, value) {
    const preview = this._ownElement(goalEl, '[data-goals-v3-editor-target="goalPreview"]');
    if (!preview) return;

    const text = value.trim();
    if (text) {
      preview.textContent = text;
    } else {
      preview.innerHTML = '';
      const placeholder = document.createElement('em');
      placeholder.className = 'text-muted';
      placeholder.textContent = preview.dataset.placeholder || '';
      preview.appendChild(placeholder);
    }
  }

  // --- Trial pairs -----------------------------------------------------

  // A Trial / percentage goal's baseline, start of care and target are
  // correct-out-of-total pairs, held to what a session's trial line holds:
  // the correct box refuses to submit more than the total (the total's own
  // min of 1 refuses a total of 0). Fired from either half of the pair.
  checkTrialsPair(event) {
    const pair = event.currentTarget.closest('[data-goals-v3-trials-pair]');
    if (pair) this._checkTrialsPair(pair);
  }

  _checkTrialsPair(pair) {
    const correctInput = pair.querySelector('input[name$="[correct]"]');
    const totalInput = pair.querySelector('input[name$="[total]"]');
    if (!correctInput || !totalInput) return;

    const correct = correctInput.value !== '' ? Number(correctInput.value) : null;
    const total = totalInput.value !== '' ? Number(totalInput.value) : null;
    const over = correct !== null && total !== null && correct > total;
    correctInput.setCustomValidity(over ? 'Correct cannot be more than the total trials.' : '');
  }

  // --- Framework swap --------------------------------------------------

  // Fired by Impulse's awc-autocomplete:commit event when the framework
  // picker selection changes. The selected option element is at
  // `event.detail.target`; the new framework id is its `value` attribute.
  async frameworkChanged(event) {
    const ac = event.currentTarget;
    const target = event.detail && event.detail.target;
    const frameworkId = target ? target.getAttribute('value') : null;
    if (!frameworkId) return;

    const goalEl = ac.closest('[data-goal-key]');
    if (!goalEl) return;

    this._describeFramework(goalEl, frameworkId);
    const disabled = ac.hasAttribute('disabled') ? 'true' : 'false';
    await this._refreshRegion(goalEl, frameworkId, disabled);
  }

  // Refresh the framework info icon's tooltip, and the picker's hidden
  // description that says the same (goals_v3/_label_tip), to match the new
  // selection. Descriptions ride along as a JSON map on the wrapper rather
  // than per-option attrs, since Impulse's option components don't pass
  // arbitrary data attrs through to the rendered <li>.
  _describeFramework(goalEl, frameworkId) {
    const wrap = this._ownElement(goalEl, '[data-framework-descriptions]');
    let description = '';
    if (wrap && wrap.dataset.frameworkDescriptions) {
      try {
        const map = JSON.parse(wrap.dataset.frameworkDescriptions);
        description = map[frameworkId] || '';
      } catch (e) {
        /* malformed — leave description empty */
      }
    }
    const infoIcon = this._ownElement(goalEl, '.goals-v3-framework-info');
    if (infoIcon) {
      infoIcon.setAttribute('title', description);
      infoIcon.setAttribute('data-original-title', description);
      if (window.$) window.$(infoIcon).tooltip('dispose').tooltip();
    }
    const tip = this._ownElement(goalEl, '.goals-v3-framework-tip');
    if (tip) tip.textContent = description;
  }

  // Fired by the region's second-pick dropdown (frameworks v2). What a
  // change means depends on the framework, declared by the region as
  // data-second-pick-behavior — always handled in place, never by a
  // re-render, so typed and stored values survive the change of mind:
  //   relabel:       a Numeric unit pick only renames the suffix beside the
  //                  blanks.
  //   swap_target:   a Trial / percentage target-form pick toggles the two
  //                  pre-rendered target variants; only the active variant's
  //                  inputs are enabled, so exactly one [target] submits.
  //   relabel_words: a Binomial word pick renames the two options of each
  //                  value select; the values stay 'true' and 'false', so
  //                  what was chosen stays chosen.
  secondPickChanged(event) {
    const target = event.detail && event.detail.target;
    const picked = target ? target.getAttribute('value') : null;
    if (picked === null) return;

    this._applySecondPick(event.currentTarget, picked);
  }

  _applySecondPick(ac, picked) {
    // The pick lives in its own Details-band column; the blanks it governs
    // are the goal's measurement region row below.
    const wrap = ac.closest('.goals-v3-second-pick');
    const goalEl = ac.closest('[data-goal-key]');
    const region = goalEl && this._ownElement(goalEl, '[data-goals-v3-editor-target="measurementRegion"]');
    if (!wrap || !region) return;

    const behavior = wrap.dataset.secondPickBehavior;
    if (behavior === 'relabel') {
      let suffix = '';
      try {
        const map = JSON.parse(wrap.dataset.suffixMap || '{}');
        suffix = map[picked] || '';
      } catch (e) {
        /* malformed — treat as unitless */
      }
      region.querySelectorAll('[data-goals-v3-unit-suffix]').forEach((span) => {
        span.textContent = suffix;
        const append = span.closest('.input-group-append');
        if (append) append.hidden = suffix === '';
      });
    } else if (behavior === 'swap_target') {
      region.querySelectorAll('[data-goals-v3-target-variant]').forEach((variant) => {
        const active = variant.dataset.goalsV3TargetVariant === picked;
        variant.hidden = !active;
        variant.querySelectorAll('input, select').forEach((input) => {
          input.disabled = !active;
        });
      });
    } else if (behavior === 'relabel_words') {
      let words = null;
      try {
        words = JSON.parse(wrap.dataset.wordsMap || '{}')[picked] || null;
      } catch {
        /* malformed: leave the words as they are */
      }
      if (!words) return;
      region.querySelectorAll('[data-goals-v3-binary-field] awc-autocomplete').forEach((select) => {
        this._relabelBinary(select, words);
      });
    }
  }

  // A Binomial value select wears the picked words: 'false' reads as the fail
  // word and 'true' as the pass word, in each option and in the chosen value
  // Impulse shows (and puts back on blur, from the hidden field's data-text).
  _relabelBinary(select, words) {
    const wordFor = { true: words.pass, false: words.fail };
    select.querySelectorAll('[role="option"]').forEach((option) => {
      const word = wordFor[option.getAttribute('value')];
      if (word === undefined) return;
      option.dataset.text = word;
      const text = option.querySelector('.awc-autocomplete-option-text');
      if (text) {
        text.textContent = word;
      } else {
        option.textContent = word;
      }
    });

    const hidden = select.querySelector('input[data-behavior="hidden-field"]');
    const word = hidden ? wordFor[hidden.value] : undefined;
    if (word === undefined) return;
    hidden.dataset.text = word;
    const input = select.querySelector('.awc-autocomplete-input');
    if (input) input.value = word;
  }

  // Fetches a fresh framework envelope and distributes its two parts into
  // the goal's slots: the second-pick column beside the framework picker,
  // and the value-blanks row below. Plain same-origin fetch (session cookie
  // auth) — not Turbo, since the main app pack loads Turbolinks. A sequence
  // stamp on the region drops out-of-order responses, failures included, so
  // rapid framework flips can't leave a stale framework's blanks behind the
  // committed framework_id, or put the old framework back over a newer pick.
  async _refreshRegion(goalEl, frameworkId, disabled) {
    const region = this._ownElement(goalEl, '[data-goals-v3-editor-target="measurementRegion"]');
    if (!region) return;

    const seq = String(Number(region.dataset.gv3FetchSeq || 0) + 1);
    region.dataset.gv3FetchSeq = seq;

    // Lock the old blanks (and pick) while the new framework's arrive: a fast
    // click could otherwise land an edit in a field the committed framework
    // no longer means. The locks live in a per-region registry so overlapping
    // swaps compose: only the CURRENT call resolves the registry — a
    // successful apply replaces the locked nodes wholesale, a failure reopens
    // everything locked across the overlapping calls, and a superseded
    // response leaves the registry to the call that superseded it.
    const locks = (this._regionLocks = this._regionLocks || new Map());
    const pickSlot = this._ownElement(goalEl, '[data-goals-v3-editor-target="pickSlot"]');
    const lockedNow = [];
    [region, pickSlot].forEach((slot) => {
      if (!slot) return;
      // Impulse selects (the second pick included) drive themselves off the
      // <awc-autocomplete> element's own disabled attribute — disabling only
      // their inner inputs leaves the dropdown openable, so the component
      // itself joins the lock.
      slot.querySelectorAll('input, select, textarea, button, awc-autocomplete').forEach((el) => {
        if (el.tagName === 'AWC-AUTOCOMPLETE') {
          if (el.hasAttribute('disabled')) return;
          el.setAttribute('disabled', '');
        } else {
          if (el.disabled) return;
          el.disabled = true;
        }
        lockedNow.push(el);
      });
    });
    locks.set(region, (locks.get(region) || []).concat(lockedNow));

    const unlockIfCurrent = () => {
      if (region.dataset.gv3FetchSeq !== seq) return;
      (locks.get(region) || []).forEach((el) => {
        if (el.tagName === 'AWC-AUTOCOMPLETE') {
          el.removeAttribute('disabled');
        } else {
          el.disabled = false;
        }
      });
      locks.delete(region);
    };

    // A failure puts the applied framework back only while it is still the
    // latest request, the same stamp check the success path makes. An older
    // request failing after a newer pick would otherwise put the old framework
    // over that pick, and when the newer load then succeeded the form would
    // save the old framework with the new framework's blanks.
    const failIfCurrent = () => {
      if (region.dataset.gv3FetchSeq !== seq) return;
      unlockIfCurrent();
      this._restoreFramework(goalEl, region);
    };

    const url = new URL(`/goals-v3/framework-region/${frameworkId}`, window.location.origin);
    url.searchParams.set('attr_base', region.dataset.attrBase);
    url.searchParams.set('disabled', disabled);

    try {
      // X-Requested-With makes an expired session answer 401 instead of
      // redirecting to the sign-in page; a redirect that does arrive is a
      // failure like any other, and so is a 200 with no region in it: the
      // page would otherwise be poured into the goal row and remembered as
      // the applied framework.
      const response = await fetch(url.toString(), {
        headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!response.ok || response.redirected) {
        failIfCurrent();
        return;
      }
      const html = await response.text();
      if (region.dataset.gv3FetchSeq !== seq) return;
      if (!this._looksLikeRegion(html)) {
        failIfCurrent();
        return;
      }
      this._applyRegionParts(goalEl, region, html);
      this._noteFrameworkApplied(goalEl, frameworkId);
      locks.delete(region);
    } catch (e) {
      // Reopen the existing region in place and put the previously applied
      // framework back behind it: the old blanks only mean anything under the
      // old framework, and a mismatched pair would otherwise surface as a
      // rejected save. The row says what happened so the therapist can retry.
      failIfCurrent();
    }
  }

  // --- Framework memo --------------------------------------------------
  // Each goal card remembers the framework its region was last rendered for
  // (the server render, then every successful swap), so a failed swap can put
  // the picker back where the blanks still make sense.

  _rememberFrameworks() {
    this.element.querySelectorAll('[data-goal-key]').forEach((goalEl) => {
      if (goalEl.dataset.gv3AppliedFramework !== undefined) return;
      const input = this._frameworkInput(goalEl);
      if (input) this._noteFrameworkApplied(goalEl, input.value);
    });
  }

  // The first element matching the selector that belongs to this goal itself.
  // A long-term goal's card holds its short-term goals' cards, and a plain
  // querySelector would reach into them: the parent's framework note would be
  // written into (or cleared from) a child's row.
  _ownElement(goalEl, selector) {
    return Array.from(goalEl.querySelectorAll(selector)).find((el) => el.closest('[data-goal-key]') === goalEl) || null;
  }

  _frameworkInput(goalEl) {
    return this._ownElement(goalEl, 'input[name$="[framework_id]"]');
  }

  _noteFrameworkApplied(goalEl, frameworkId) {
    if (frameworkId === undefined || frameworkId === null) return;
    goalEl.dataset.gv3AppliedFramework = frameworkId;
    const note = this._ownElement(goalEl, '.gv3-region-fetch-error');
    if (note) note.remove();
  }

  // The picker is put back through its own setValue. Impulse keeps a choice
  // in three places (the hidden field that submits, the name in view, and
  // that field's data-text, which it puts back in view each time the list
  // closes), and setValue writes all three, naming the framework from the
  // picker's own option. Writing only the first two left data-text on the
  // failed framework, so the picker showed it again when the list next closed
  // while the form still submitted the old one.
  _restoreFramework(goalEl, region) {
    const previous = goalEl.dataset.gv3AppliedFramework;
    const input = this._frameworkInput(goalEl);
    if (previous !== undefined && input && input.value !== previous) {
      const picker = this._ownElement(goalEl, '[data-goals-v3-editor-target="frameworkSelect"]');
      if (picker) picker.setValue(previous);
    }
    let note = this._ownElement(goalEl, '.gv3-region-fetch-error');
    if (!note) {
      note = document.createElement('div');
      note.className = 'small text-danger mt-1 gv3-region-fetch-error';
      note.setAttribute('role', 'alert');
      region.insertAdjacentElement('beforebegin', note);
    }
    note.textContent = 'Could not load this framework, so the previous one is still selected. Please try again.';
  }

  // Splits the fetched envelope (data-region-part="pick" / "blanks") into
  // the goal's two slots and re-applies the Details-band geometry: four
  // col-md-3 columns when the framework takes a second pick, three
  // col-md-4 when it doesn't.
  // A region response carries the blanks part; anything else (a sign-in page
  // after a redirect the browser followed, an error page) is not one.
  _looksLikeRegion(html) {
    const parsed = document.createElement('template');
    parsed.innerHTML = html;
    return parsed.content.querySelector('[data-region-part="blanks"]') !== null;
  }

  _applyRegionParts(goalEl, region, html) {
    const parsed = document.createElement('template');
    parsed.innerHTML = html;
    const pickPart = parsed.content.querySelector('[data-region-part="pick"]');
    const blanksPart = parsed.content.querySelector('[data-region-part="blanks"]');
    region.innerHTML = blanksPart ? blanksPart.innerHTML : html;

    const pickSlot = this._ownElement(goalEl, '[data-goals-v3-editor-target="pickSlot"]');
    if (!pickSlot) return;
    const pickHtml = pickPart ? pickPart.innerHTML : '';
    const hasPick = pickHtml.trim() !== '';
    pickSlot.innerHTML = pickHtml;
    pickSlot.classList.toggle('d-none', !hasPick);

    const detailRow = pickSlot.closest('.row');
    if (!detailRow) return;
    detailRow.querySelectorAll(':scope > [data-goals-v3-editor-detail-col]').forEach((col) => {
      col.classList.toggle('col-md-3', hasPick);
      col.classList.toggle('col-md-4', !hasPick);
    });
  }

  // --- Goal bank ("Add goal from goal bank", legacy parity) -------------
  // The per-card link opens the shared #goalBankModal with that card's
  // textarea as the paste target. Search posts to goals#index as JS — the
  // response repaints #goal_bank_results with Select buttons, exactly the
  // legacy editor's flow — and "Select goal" pastes the bank goal's
  // rendered text (patient name already substituted) into the target,
  // stamping bank provenance on new cards for DocumentReconciler.

  // A long-term goal's card holds its short-term goals' cards, so every
  // lookup here finds only the picking goal's own element (_ownElement).
  openGoalBank(event) {
    event.preventDefault();
    const card = event.currentTarget.closest('.goal-parent');
    const textarea = card ? this._ownElement(card, 'textarea.ltg-goal-text') : null;
    const modal = this._goalBankModal();
    if (!textarea || !modal) return;
    this._bankTarget = textarea;
    this._wireGoalBank(modal);
    const $ = window.jQuery || window.$;
    if ($ && $.fn && $.fn.modal) {
      $(modal).modal('show');
    } else {
      modal.classList.add('show');
      modal.style.display = 'block';
    }
  }

  _goalBankModal() {
    return this.element.querySelector('#goalBankModal');
  }

  // Listeners bind once, on first open, and delegate from the modal so the
  // repainted results container needs no rebinding.
  _wireGoalBank(modal) {
    if (this._bankWired) return;
    this._bankWired = true;

    let timeout = null;
    const debouncedSearch = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => this._searchGoalBank(), 800);
    };

    this._onBankKeydown = (e) => {
      if (e.key === 'Enter' && e.target.closest('input.goal-bank-query')) e.preventDefault();
    };
    this._onBankInput = (e) => {
      if (e.target.closest('input.goal-bank-query')) debouncedSearch();
    };
    this._onBankClick = (e) => {
      if (e.target.closest('button.goal-bank-query-button')) {
        this._searchGoalBank();
        return;
      }
      const select = e.target.closest('button.select-goal');
      if (select) this._pasteBankGoal(select);
    };
    modal.addEventListener('keydown', this._onBankKeydown);
    modal.addEventListener('input', this._onBankInput);
    modal.addEventListener('click', this._onBankClick);
  }

  _searchGoalBank() {
    const modal = this._goalBankModal();
    const $ = window.jQuery || window.$;
    if (!modal || !$ || !$.ajax) return;
    const value = (selector) => {
      const el = modal.querySelector(selector);
      return el ? el.value : '';
    };
    const button = modal.querySelector('button.goal-bank-query-button');
    $.ajax({
      url: button && button.dataset.url ? button.dataset.url : '/goals',
      type: 'GET',
      dataType: 'script',
      data: {
        q: value('input.goal-bank-query'),
        mine: modal.querySelector('input.goal-bank-mine:checked') ? '1' : '0',
        goal_horizon_id: value('select.goal-bank-horizon'),
        discipline_id: value('select.goal-bank-discipline'),
        major_focus_area_tag_id: value('select.goal-bank-major'),
        minor_focus_area_tag_id: value('select.goal-bank-minor'),
        name: button ? button.dataset.name : '',
        paste_control_id: this._bankTarget && this._bankTarget.id ? this._bankTarget.id : 'goals-v3-editor',
      },
    });
  }

  _pasteBankGoal(button) {
    const source = document.getElementById(button.dataset.sourceId || '');
    const textarea = this._bankTarget;
    const modal = this._goalBankModal();
    if (!source || !textarea) return;
    textarea.value = source.textContent.trim();
    // Bubbling input runs syncGoalPreview so the collapsed header preview
    // updates, and lets the autoresize hook grow the textarea.
    textarea.dispatchEvent(new Event('input', { bubbles: true }));
    // Provenance rides only on a new goal's own field: a saved goal has none
    // of its own, and keeps the bank goal it was born from.
    const card = textarea.closest('.goal-parent');
    const bankInput = card ? this._ownElement(card, '[data-goals-v3-editor-bank-goal]') : null;
    if (bankInput) bankInput.value = (button.dataset.sourceId || '').replace(/^goal_/, '');
    const $ = window.jQuery || window.$;
    if (modal && $ && $.fn && $.fn.modal) {
      $(modal).modal('hide');
    } else if (modal) {
      modal.classList.remove('show');
      modal.style.display = 'none';
    }
  }

  // --- Restoring a copy auto-save kept ---------------------------------
  // auto-save:restore hands back a copy of the editor kept by auto-save, the
  // session-timeout save or the offline save: what it would have submitted
  // then (helpers/form-region.js). The goals come back as the copy had them.
  // A goal written since the page loaded (its id is blank) comes back from
  // the row template under its own key, and one added back from "Goals taken
  // off before signing" from its offer; a row the page added since the copy
  // goes. A saved goal this document is writing follows the copy too, as the
  // trash can take it off: a copy without it takes it off, without asking,
  // and one made before it came off brings its row back. Any other saved
  // goal's row always stays, as the editor never removes one. A saved goal
  // the page no longer lists (closed or deleted since) stays off, as it
  // would on a reload. A goal whose copy has another framework loads it
  // first, as picking it does; one that cannot (the load fails, or its
  // framework is fixed) keeps the framework it shows, and the copy's values
  // for the other framework stay out. Then every value, pick, status and
  // comment goes back.

  async restore(event) {
    if (!this.hasCanvasTarget) return;
    const fields = (event.detail && event.detail.fields) || [];
    const prefix = this.element.dataset.autoSaveRegion;
    const goals = this._copiedGoals(fields, prefix);

    this._restoreRows(goals);
    const kept = await this._restoreFrameworks(goals, new Map(fields));
    const onKeptFramework = (name) =>
      kept.some((base) => name === `${base}[framework_id]` || name.startsWith(`${base}[measurement_config]`));
    fillRegion(
      this.element,
      fields.filter(([name]) => !onKeptFramework(name)),
      prefix
    );

    // The second pick's choice shapes the blanks (the target shown, the
    // words, the unit), as picking it does.
    this._copiedRows(goals).forEach(([, row]) => {
      const pick = this._ownElement(row, '.goals-v3-second-pick awc-autocomplete');
      const picked = pick && pick.querySelector('input[data-behavior="hidden-field"]');
      if (picked && picked.value) this._applySecondPick(pick, picked.value);
      const text = this._ownElement(row, 'textarea.ltg-goal-text');
      if (text) this._showPreview(row, text.value);
    });
    this.element.querySelectorAll('[data-goals-v3-trials-pair]').forEach((pair) => this._checkTrialsPair(pair));
    recheckRangePairs(this.application, this.element);
    this._renumberPreviews();
    this._syncGoalsMasterCaret();
  }

  // The copy's goals in page order, each long-term goal with its short-term
  // goals: [{ key, base, id, stgs: [{ key, base, id }] }]. id is the goal's
  // own, blank for a goal written on the page and saved nowhere yet.
  _copiedGoals(fields, prefix) {
    const values = new Map(fields);
    const ltgs = new Map();
    fields.forEach(([name]) => {
      const [key, part, stgKey] = nameSegments(name, prefix);
      if (!key) return;
      if (!ltgs.has(key)) {
        const base = `${prefix}[${key}]`;
        ltgs.set(key, { key, base, id: values.get(`${base}[id]`), stgs: new Map() });
      }
      const ltg = ltgs.get(key);
      if (part === 'short_term_goals' && stgKey && !ltg.stgs.has(stgKey)) {
        const base = `${ltg.base}[short_term_goals][${stgKey}]`;
        ltg.stgs.set(stgKey, { key: stgKey, base, id: values.get(`${base}[id]`) });
      }
    });
    return Array.from(ltgs.values(), (ltg) => ({ ...ltg, stgs: Array.from(ltg.stgs.values()) }));
  }

  // The page's rows for the copy's goals, as [goal, row] pairs.
  _copiedRows(goals) {
    return goals.flatMap((ltg) => {
      const ltgRow = this._rowIn(this.canvasTarget, ltg.key);
      if (!ltgRow) return [];
      const stgCanvas = this._ownElement(ltgRow, '.short-term-goals-canvas');
      const stgRows = ltg.stgs
        .map((stg) => [stg, stgCanvas && this._rowIn(stgCanvas, stg.key)])
        .filter(([, row]) => row);
      return [[ltg, ltgRow], ...stgRows];
    });
  }

  _restoreRows(goals) {
    const ltgKeys = new Set(goals.map((ltg) => ltg.key));
    this._rowsIn(this.canvasTarget).forEach((row) => {
      if (!ltgKeys.has(row.dataset.goalKey)) this._takeOff(row);
    });

    goals.forEach((ltg) => {
      const template = this.hasLtgTemplateTarget ? this.ltgTemplateTarget : null;
      const row =
        this._rowIn(this.canvasTarget, ltg.key) || this._bringBack(ltg, this.canvasTarget, template, '__KEY__');
      const stgCanvas = row && this._ownElement(row, '.short-term-goals-canvas');
      if (!stgCanvas) return;

      const stgKeys = new Set(ltg.stgs.map((stg) => stg.key));
      this._rowsIn(stgCanvas).forEach((stgRow) => {
        if (!stgKeys.has(stgRow.dataset.goalKey)) this._takeOff(stgRow);
      });
      const stgTemplate = this._ownElement(row, 'template.goals-v3-stg-template');
      ltg.stgs.forEach((stg) => {
        if (!this._rowIn(stgCanvas, stg.key)) this._bringBack(stg, stgCanvas, stgTemplate, '__STG_KEY__');
      });
      placeInOrder(ltg.stgs.map((stg) => this._rowIn(stgCanvas, stg.key)).filter(Boolean));
    });
    placeInOrder(goals.map((ltg) => this._rowIn(this.canvasTarget, ltg.key)).filter(Boolean));

    const noGoalsMessage = this.element.querySelector('.no-long-term-goals');
    if (noGoalsMessage) noGoalsMessage.classList.toggle('hidden', this._rowsIn(this.canvasTarget).length > 0);
    this._renumberPreviews();
  }

  // The goal rows directly in a canvas (a long-term goal's row holds its
  // short-term goals' rows).
  _rowsIn(canvas) {
    return Array.from(canvas.children).filter((el) => el.matches('[data-goal-key]'));
  }

  _rowIn(canvas, key) {
    return this._rowsIn(canvas).find((row) => row.dataset.goalKey === key) || null;
  }

  // A row the copy does not have goes when the page added it (a goal written
  // on the page, whose id is blank, or one added back from "Goals taken off
  // before signing", which goes back on offer) and when it is a saved goal
  // this document is writing, which the trash could take off too.
  _takeOff(row) {
    const id = this._ownElement(row, 'input[name$="[id]"]');
    const pageAdded = row.dataset.withdrawnGoalId || (id && id.value === '');
    if (!pageAdded && !this._savedTakeOff(row)) return;
    this._detachRow(row);
  }

  // A row the copy has and the page does not: a goal written on the page
  // comes from the row template under its own key (never a key the page
  // could not have made, which would go into the markup), a saved goal taken
  // off since the copy was made from the row kept aside, and a goal added
  // back from "Goals taken off before signing" from its offer.
  _bringBack(goal, canvas, template, placeholder) {
    const takenOff = goal.id ? this._takenOff.get(goal.key) : null;
    const takenOffId = takenOff ? this._ownElement(takenOff, 'input[name$="[id]"]') : null;
    if (goal.id === '') {
      if (!template || !isPlainKey(goal.key)) return null;
      canvas.insertAdjacentHTML('beforeend', template.innerHTML.replaceAll(placeholder, goal.key));
    } else if (takenOffId && takenOffId.value === goal.id) {
      this._takenOff.delete(goal.key);
      canvas.appendChild(takenOff);
      takenOff
        .querySelectorAll('[data-withdrawn-goal-id]')
        .forEach((added) => this._setWithdrawnOffered(added.dataset.withdrawnGoalId, false));
      if (takenOff.matches('[data-goals-v3-editor-target="longTermGoal"]')) this._syncOffersUnder(goal.key);
    } else {
      const offer = Array.from(this.element.querySelectorAll('template[data-withdrawn-goal-template]')).find(
        (candidate) => goal.id && candidate.dataset.withdrawnGoalTemplate === goal.id
      );
      if (!offer) return null;
      canvas.insertAdjacentHTML('beforeend', offer.innerHTML);
      this._setWithdrawnOffered(goal.id, false);
    }
    return this._rowIn(canvas, goal.key);
  }

  // Each copied goal whose framework is not the one its row shows loads it,
  // all at once. Returns the name prefixes of the goals left on the
  // framework they show.
  async _restoreFrameworks(goals, values) {
    const kept = [];
    await Promise.all(
      this._copiedRows(goals).map(async ([goal, row]) => {
        const wanted = values.get(`${goal.base}[framework_id]`);
        const input = this._frameworkInput(row);
        if (wanted === undefined || !input || input.value === wanted) return;

        const picker = this._ownElement(row, '[data-goals-v3-editor-target="frameworkSelect"]');
        if (!picker) {
          kept.push(goal.base);
          return;
        }
        writeField(input, wanted);
        this._describeFramework(row, wanted);
        await this._refreshRegion(row, wanted, picker.hasAttribute('disabled') ? 'true' : 'false');
        if (this._frameworkInput(row).value !== wanted) kept.push(goal.base);
      })
    );
    return kept;
  }

  // --- Helpers ---------------------------------------------------------

  _generateKey() {
    return `new-${Math.random().toString(36).slice(2, 10)}-${Date.now().toString(36)}`;
  }
}
